import type { PortalArticlePayload } from '../portal/portalDraftEnvelope';
import { tryParsePortalDraft } from '../portal/portalDraftEnvelope';

/** Plain text derived from an article payload (title, excerpt, body blocks). */
export function articlePayloadPlainText(p: PortalArticlePayload): string {
  const parts: string[] = [];
  if (p.title) parts.push(p.title);
  if (p.excerpt) parts.push(p.excerpt);
  const blocks = p.content_blocks || [];
  const blockTexts = blocks
    .map((b) => {
      const t = (b.text || '').trim();
      if (t) return t;
      if ((b.type || '').toLowerCase() === 'image' && (b.src || '').trim()) {
        return ((b.description || b.src) as string).trim();
      }
      return '';
    })
    .filter(Boolean);
  if (blockTexts.length) {
    parts.push(blockTexts.join('\n'));
  } else if (p.content?.trim()) {
    parts.push(p.content.trim());
  }
  return parts.join('\n');
}

/** Article body only (no title / excerpt) — for UI that shows excerpt separately. */
export function articlePayloadMainBodyPlain(p: PortalArticlePayload): string {
  const blocks = p.content_blocks || [];
  const blockTexts = blocks
    .map((b) => {
      const t = (b.text || '').trim();
      if (t) return t;
      if ((b.type || '').toLowerCase() === 'image' && (b.src || '').trim()) {
        return ((b.description || b.src) as string).trim();
      }
      return '';
    })
    .filter(Boolean);
  if (blockTexts.length) {
    return blockTexts.join('\n\n');
  }
  if (p.content?.trim()) {
    return p.content.trim();
  }
  return '';
}

function truncatePreview(s: string, maxChars: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= maxChars) return t;
  return `${t.slice(0, Math.max(0, maxChars - 1))}…`;
}

/** Short snippet for list cards (Ready to publish, etc.). Never returns raw portal JSON. */
export function draftListPreviewSnippet(draftContent: string, maxChars = 280): string {
  const raw = (draftContent || '').trim();
  if (!raw) return '';
  const env = tryParsePortalDraft(raw);
  if (env?.kind === 'article') {
    const ex = (env.payload.excerpt || '').trim();
    if (ex) return truncatePreview(ex, maxChars);
    const body = articlePayloadMainBodyPlain(env.payload);
    if (body) return truncatePreview(body, maxChars);
    return truncatePreview(env.payload.title || '', maxChars);
  }
  if (env?.kind === 'recipe') {
    const d = (env.payload.description || env.payload.title || '').trim();
    return truncatePreview(d, maxChars);
  }
  if (env?.kind === 'movie') {
    const d = (env.payload.plot_summary || env.payload.notes || env.payload.movie_title || '').trim();
    return truncatePreview(d, maxChars);
  }
  if (env?.kind === 'event') {
    const blocks = env.payload.content_blocks || [];
    const txt = blocks
      .map((b) => (b.text || b.html || '').trim())
      .filter(Boolean)
      .join(' ');
    return truncatePreview(txt || env.payload.title || '', maxChars);
  }
  return truncatePreview(raw, maxChars);
}

export type PortalArticleDisplayParts = {
  isPortalArticle: boolean;
  excerpt: string;
  body: string;
};

/** Excerpt + main body for article dialog; non-portal drafts return raw content as body. */
export function portalArticleDisplayParts(draftContent: string): PortalArticleDisplayParts {
  const raw = (draftContent || '').trim();
  const env = tryParsePortalDraft(raw);
  if (env?.kind === 'article') {
    return {
      isPortalArticle: true,
      excerpt: (env.payload.excerpt || '').trim(),
      body: articlePayloadMainBodyPlain(env.payload),
    };
  }
  return { isPortalArticle: false, excerpt: '', body: raw };
}

/** Words / characters / reading time (same 230 wpm rule as DistributionHub). */
export function plainTextStats(text: string): {
  words: number;
  characters: number;
  readingTimeMinutes: number;
} {
  const trimmed = text.trim();
  const words = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
  const characters = text.length;
  const readingTimeMinutes = words === 0 ? 0 : Math.max(1, Math.ceil(words / 230));
  return { words, characters, readingTimeMinutes };
}

/** Flatten portal draft JSON into plain text for metrics (any kind). */
export function articleBodyTextForStats(draftContent: string): string {
  const env = tryParsePortalDraft(draftContent.trim());
  if (!env) return draftContent;
  switch (env.kind) {
    case 'article':
      return articlePayloadPlainText(env.payload);
    case 'recipe': {
      const r = env.payload;
      return [r.title, r.description, ...r.ingredients, ...r.steps].filter(Boolean).join('\n');
    }
    case 'movie': {
      const m = env.payload;
      return [m.movie_title, m.plot_summary, m.notes, ...(m.discussion_questions || [])].filter(Boolean).join('\n');
    }
    case 'event': {
      const ev = env.payload;
      const blocks = (ev.content_blocks || [])
        .map((b) => (b.text || b.html || '').trim())
        .filter(Boolean);
      return [ev.title, ev.location, ...blocks].filter(Boolean).join('\n');
    }
    default:
      return draftContent;
  }
}
